"use strict";
// Copyright © Veeam Software Group GmbH
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.CollectionModel = void 0;
const fast_deep_equal_1 = __importDefault(require("fast-deep-equal"));
const rxjs_1 = require("rxjs");
const core_1 = require("@veeam/core");
const FiltersPersistLocalStorage_1 = require("./FiltersPersistLocalStorage");
const types_1 = require("./types");
class CollectionModel extends core_1.LoadableModel {
    constructor(idGetter, loader, initialValue) {
        const value = {};
        Object.assign(value, initialValue, {
            items: [],
            filters: new core_1.FilterQuery([]),
            selection: [],
            checked: [],
            expanded: [],
            pagination: null,
            sorting: []
        });
        super(loader, value);
        this.valueType = {};
        this.loaderType = {};
        this.getItemID = idGetter;
        this.filtersPersistStorage = null;
        this.waitForReadFilters = null;
        this.isFiltersRead = false;
        this.shouldActualizeSelection = true;
        this.filtersPersistStorageNameSpace = '';
        this.waitInitOperationStack = [];
        this.buildDataIndex();
    }
    initUpdatePoolRelease() {
        this.waitInitOperationStack.forEach((cb) => cb());
        this.waitInitOperationStack = [];
    }
    withInitUpdatePool(operation) {
        if (this.waitForReadFilters) {
            this.waitInitOperationStack.push(operation);
            return;
        }
        operation();
    }
    syncUpdateData(nextValue) {
        this.setValue(Object.assign(Object.assign({}, nextValue), { selection: this.getValue().selection }));
        if (this.shouldActualizeSelection) {
            this.actualizeSelection();
        }
    }
    readFilters() {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.filtersPersistStorage || this.isFiltersRead) {
                this.initUpdatePoolRelease();
                return;
            }
            this.isFiltersRead = true;
            const data = yield this.filtersPersistStorage.read(this.filtersPersistStorageNameSpace);
            if (data) {
                this.update((prev) => {
                    prev.filters = prev.filters.fromArray(data.filters);
                    prev.sorting = data.sorting;
                });
            }
            (_a = this.filterAndSortingSubscription) === null || _a === void 0 ? void 0 : _a.unsubscribe();
            this.filterAndSortingSubscription = (0, rxjs_1.merge)(this.fieldChanges('filters'), this.fieldChanges('sorting')).subscribe(() => {
                this.saveFilters();
            });
            this.initUpdatePoolRelease();
        });
    }
    saveFilters() {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            return (_a = this.filtersPersistStorage) === null || _a === void 0 ? void 0 : _a.save(this.filtersPersistStorageNameSpace, {
                filters: this.getValue()
                    .filters.getFilterList()
                    .filter((item) => {
                    return item.type !== types_1.COLLECTION_MODEL_FILTER_TYPES.search;
                }),
                sorting: this.getValue().sorting
            });
        });
    }
    buildDataIndex() {
        this.buildItemIndex();
        this.buildSelectionIndex();
    }
    buildItemIndex() {
        this.itemIndex = {};
        const items = this.getValue().items;
        const end = items.length;
        for (let i = 0; i < end; i++) {
            const item = items[i];
            this.itemIndex[String(this.getItemID(item))] = i;
        }
    }
    buildSelectionIndex() {
        this.selectionIndex = {};
        const items = this.getValue().selection;
        const end = items.length;
        for (let i = 0; i < end; i++) {
            const item = items[i];
            this.selectionIndex[String(item)] = true;
        }
    }
    withPagination(pagination) {
        this.update((prevValue) => {
            prevValue.pagination = pagination;
        });
        return this;
    }
    withSorting(sorting) {
        this.update((prevValue) => {
            prevValue.sorting = [sorting];
        });
        return this;
    }
    withFiltersFromArray(filters) {
        this.update((prevValue) => {
            prevValue.filters = core_1.FilterQuery.fromArray(filters);
        });
        return this;
    }
    withFiltersFromGroups(filterGroups) {
        this.update((prevValue) => {
            prevValue.filters = core_1.FilterQuery.fromGroups(filterGroups);
        });
        return this;
    }
    withFilters(filters) {
        this.update((prevValue) => {
            prevValue.filters = filters;
        });
        return this;
    }
    withAdditionalValues(additionalValues) {
        this.update((prevValue) => {
            const keys = Object.keys(additionalValues);
            keys.forEach((key) => {
                prevValue[key] = additionalValues[key];
            });
        });
        return this;
    }
    getItemById(id) {
        const items = this.getValue().items;
        const idx = this.itemIndex[String(id)];
        if (typeof idx !== 'number') {
            return null;
        }
        return items[idx] || null;
    }
    getSelectedItems() {
        const { selection } = this.getValue();
        return selection.reduce((items, id) => {
            const item = this.getItemById(id);
            if (item !== null) {
                items.push(item);
            }
            return items;
        }, []);
    }
    setFilter(filterIndex, filter) {
        return __awaiter(this, void 0, void 0, function* () {
            this.withInitUpdatePool(() => {
                this.update((prevValue) => {
                    if (!filter) {
                        prevValue.filters = prevValue.filters.mapFilter(filterIndex, null);
                        return;
                    }
                    prevValue.filters = prevValue.filters.mapFilter(filterIndex, (prevFilter) => (Object.assign(Object.assign({}, prevFilter), filter)));
                });
            });
        });
    }
    setShouldActualizeSelection(value) {
        this.shouldActualizeSelection = value;
    }
    actualizeSelection() {
        const currentPageIDs = this.getValue().items.reduce((acc, curr) => {
            const id = this.getItemID(curr);
            acc[String(id)] = true;
            return acc;
        }, {});
        this.update((prevValue) => {
            prevValue.selection = prevValue.selection.filter((id) => currentPageIDs[String(id)]);
        });
    }
    withSyncItemComparator(comparator) {
        const defaultIdComparator = (prev, next) => {
            return (0, fast_deep_equal_1.default)(prev.map((i) => this.getItemID(i)), next.map((i) => this.getItemID(i)));
        };
        return this.withSyncComparator((prevValue, nextValue) => {
            return (comparator || defaultIdComparator)(prevValue.items, nextValue.items);
        });
    }
    withFiltersStorage(namespace, storage = new FiltersPersistLocalStorage_1.FiltersPersistLocalStorage()) {
        this.filtersPersistStorageNameSpace = namespace;
        this.filtersPersistStorage = storage;
        this.waitForReadFilters = this.readFilters().then(() => {
            this.waitForReadFilters = null;
        });
        return this;
    }
    load(payload) {
        const _super = Object.create(null, {
            load: { get: () => super.load }
        });
        return __awaiter(this, void 0, void 0, function* () {
            if (this.waitForReadFilters) {
                yield this.waitForReadFilters;
            }
            yield _super.load.call(this, payload);
            if (this.shouldActualizeSelection) {
                this.actualizeSelection();
            }
        });
    }
    sync(payload) {
        const _super = Object.create(null, {
            sync: { get: () => super.sync }
        });
        return __awaiter(this, void 0, void 0, function* () {
            if (this.waitForReadFilters) {
                yield this.waitForReadFilters;
            }
            return _super.sync.call(this, payload);
        });
    }
    destroy() {
        var _a;
        (_a = this.filterAndSortingSubscription) === null || _a === void 0 ? void 0 : _a.unsubscribe();
        super.destroy();
    }
    setValue(value) {
        super.setValue(value);
        this.buildDataIndex();
    }
    update(updater) {
        super.update(updater);
        this.buildDataIndex();
    }
    isAllItemsSelected(filter) {
        const value = this.getValue();
        const selection = value.selection;
        if (!selection.length) {
            return false;
        }
        const items = value.items;
        const end = items.length;
        for (let i = 0; i < end; i++) {
            const item = items[i];
            const key = String(this.getItemID(item));
            if (filter && !filter(item, i)) {
                continue;
            }
            if (!this.selectionIndex[key]) {
                return false;
            }
        }
        return true;
    }
    isSomeItemsSelected(filter) {
        const value = this.getValue();
        const selection = value.selection;
        if (!selection.length) {
            return false;
        }
        const items = value.items;
        const end = items.length;
        for (let i = 0; i < end; i++) {
            const item = items[i];
            const key = String(this.getItemID(item));
            if (filter && !filter(item, i)) {
                continue;
            }
            if (this.selectionIndex[key]) {
                return true;
            }
        }
        return false;
    }
}
exports.CollectionModel = CollectionModel;
//# sourceMappingURL=CollectionModel.js.map